import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { DateTime } from 'luxon';
import { forkJoin, Subject, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { ScreencastQuotaPayload } from 'src/app/entitlements/live-screencast-quota.types';
import {
  durationLabel,
  durationNote,
  formatClock,
  formatConnectedDuration,
  initials,
  isNearQuotaLimit,
  isReversedDateRange,
  presetDateRange,
  quotaAllowanceLabel,
  readableEndReason,
  displayMode,
  ScreencastDatePreset,
  statusClass,
} from 'src/app/entitlements/screencast-audit.util';
import {
  ScreencastPersonIdentity,
  ScreencastSessionStatus,
  ScreencastSessionSummary,
} from 'src/app/entitlements/screencast-sessions.types';
import { LiveScreencastQuotaService } from 'src/app/Services/live-screencast-quota.service';
import { ProjectService } from 'src/app/Services/project.service';
import { ScreencastSessionsService } from 'src/app/Services/screencast-sessions.service';
import { ScreenshotFancyboxService } from 'src/app/Services/screenshot-fancybox.service';
import {
  ScreenshotAuditEvent,
  ScreenshotAuditEventType,
  ScreenshotAuditService,
} from 'src/app/Services/screenshot-audit.service';
import {
  SettingsAuditEvent,
  SettingsAuditService,
} from 'src/app/Services/settings-audit.service';

type AuditLogsTab = 'screencast' | 'screenshot' | 'settings';

const PAGE_SIZE = 25;
const TAB_STORAGE_KEY = 'supersee.auditLogs.activeTab';
const STATUSES: Array<ScreencastSessionStatus | ''> = [
  '',
  'Live',
  'Ended',
  'Failed',
  'Dropped',
  'Connecting',
  'Incomplete',
  'Unknown',
];
const MODES = ['', 'single', 'wall', 'grid', 'patrol', 'unknown'];

interface DirectoryUser {
  id: number;
  name: string;
}

@Component({
  selector: 'app-audit-logs',
  templateUrl: './audit-logs.component.html',
  styleUrls: ['./audit-logs.component.css'],
  standalone: false,
})
export class AuditLogsComponent implements OnInit, OnDestroy {
  readonly statuses = STATUSES;
  readonly modes = MODES;
  pageSize = PAGE_SIZE;
  readonly pageSizeOptions: number[] = [25, 50, 100];
  readonly screenshotActions: Array<ScreenshotAuditEventType | ''> = ['', 'on_demand_request', 'screenshot_delete'];

  activeTab: AuditLogsTab = 'screencast';
  /** Settings audit API is not live yet. Flip this to show the tab again. */
  readonly settingsTabEnabled = false;

  filterForm: FormGroup;
  timezone = 'Asia/Kolkata';
  appliedPreset: ScreencastDatePreset = 'today';
  customStart = '';
  customEnd = '';
  dateError = '';
  appliedRangeLabel = '';
  showCustomDates = false;

  employees: any[] = [];
  requesters: any[] = [];
  userMap = new Map<number, any>();

  sessions: ScreencastSessionSummary[] = [];
  page = 1;
  total = 0;
  loading = true;
  sessionsError: string | null = null;

  quota: ScreencastQuotaPayload | null = null;
  quotaUnavailable = true;
  quotaStale = false;
  quotaLastSuccessAt: Date | null = null;
  quotaAsOfUtc: string | null = null;

  copiedSessionId: string | null = null;
  private copyResetTimer: ReturnType<typeof setTimeout> | null = null;

  screenshotEvents: ScreenshotAuditEvent[] = [];
  screenshotPage = 1;
  screenshotTotal = 0;
  screenshotLoading = false;
  screenshotError: string | null = null;

  settingsEvents: SettingsAuditEvent[] = [];
  settingsPage = 1;
  settingsTotal = 0;
  settingsLoading = false;
  settingsError: string | null = null;
  expandedSettingsId: number | null = null;

  readonly durationLabel = durationLabel;
  readonly durationNote = durationNote;
  readonly readableEndReason = readableEndReason;
  readonly displayMode = displayMode;
  readonly initials = initials;
  readonly statusClass = statusClass;
  readonly formatConnectedDuration = formatConnectedDuration;
  readonly quotaAllowanceLabel = quotaAllowanceLabel;

  private readonly destroy$ = new Subject<void>();
  private readonly sessionLoad$ = new Subject<void>();
  private readonly screenshotLoad$ = new Subject<void>();
  private readonly settingsLoad$ = new Subject<void>();
  private lastQuotaPayload: ScreencastQuotaPayload | null = null;

  constructor(
    private fb: FormBuilder,
    private projectService: ProjectService,
    private sessionsApi: ScreencastSessionsService,
    private quotaService: LiveScreencastQuotaService,
    private screenshotAudit: ScreenshotAuditService,
    private settingsAudit: SettingsAuditService,
    private screenshotFancybox: ScreenshotFancyboxService
  ) {
    this.filterForm = this.fb.group({
      employee_user_id: [''],
      admin_user_id: [''],
      mode: [''],
      status: [''],
      event_type: [''],
      datePreset: ['today'],
    });
  }

  ngOnInit(): void {
    this.activeTab = this.readStoredTab();

    this.sessionLoad$
      .pipe(
        switchMap(() => this.fetchSessions()),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.loading = false;
        if (result.error) {
          this.sessionsError = result.error;
          return;
        }
        this.sessionsError = null;
        this.sessions = result.sessions;
        this.total = result.total;
        this.page = result.page;
        if (result.timezone) {
          this.timezone = result.timezone;
        }
        this.updateRangeCaption(result.startDate, result.endDate);
      });

    this.screenshotLoad$
      .pipe(
        switchMap(() => this.fetchScreenshotEvents()),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.screenshotLoading = false;
        if (result.error) {
          this.screenshotError = result.error;
          return;
        }
        this.screenshotError = null;
        this.screenshotEvents = result.events;
        this.screenshotTotal = result.total;
        this.screenshotPage = result.page;
        if (result.timezone) {
          this.timezone = result.timezone;
        }
        this.updateRangeCaption(result.startDate, result.endDate);
      });

    this.settingsLoad$
      .pipe(
        switchMap(() => this.fetchSettingsEvents()),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.settingsLoading = false;
        if (result.error) {
          this.settingsError = result.error;
          return;
        }
        this.settingsError = null;
        this.settingsEvents = result.events;
        this.settingsTotal = result.total;
        this.settingsPage = result.page;
        if (result.timezone) {
          this.timezone = result.timezone;
        }
        this.updateRangeCaption(result.startDate, result.endDate);
      });

    this.filterForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.showCustomDates = value.datePreset === 'custom';
      if (value.datePreset === 'custom' && this.appliedPreset !== 'custom') {
        return;
      }
      if (value.datePreset && value.datePreset !== 'custom' && value.datePreset !== this.appliedPreset) {
        this.appliedPreset = value.datePreset;
        this.dateError = '';
      }
      this.page = 1;
      this.screenshotPage = 1;
      this.settingsPage = 1;
      this.refreshCurrentTab();
    });

    this.quotaService.resolveRequestTimezone().pipe(takeUntil(this.destroy$)).subscribe((tz) => {
      this.timezone = tz;
      const defaultPreset = this.defaultPresetForTab(this.activeTab);
      this.appliedPreset = defaultPreset;
      this.filterForm.patchValue({ datePreset: defaultPreset }, { emitEvent: false });
      if (defaultPreset === 'week') {
        const end = DateTime.now().setZone(tz);
        const start = end.minus({ days: 6 });
        this.customStart = start.toISODate() || '';
        this.customEnd = end.toISODate() || '';
        this.updateRangeCaption(this.customStart, this.customEnd);
      } else {
        const today = DateTime.now().setZone(tz).toISODate() || '';
        this.customStart = today;
        this.customEnd = today;
        this.updateRangeCaption(today, today);
      }
      this.loadDirectory();
      this.refreshQuota();
      this.refreshCurrentTab();
    });
  }

  ngOnDestroy(): void {
    if (this.copyResetTimer) {
      clearTimeout(this.copyResetTimer);
      this.copyResetTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange(): void {
    if (document.visibilityState !== 'visible') {
      return;
    }
    this.refreshQuota();
    this.refreshCurrentTab();
  }

  copySessionId(id: string): void {
    if (!id || !navigator?.clipboard) {
      return;
    }
    navigator.clipboard.writeText(id);
    this.copiedSessionId = id;
    if (this.copyResetTimer) {
      clearTimeout(this.copyResetTimer);
    }
    this.copyResetTimer = setTimeout(() => {
      this.copiedSessionId = null;
      this.copyResetTimer = null;
    }, 1600);
  }

  get pages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get screenshotPages(): number {
    return Math.max(1, Math.ceil(this.screenshotTotal / this.pageSize));
  }

  get settingsPages(): number {
    return Math.max(1, Math.ceil(this.settingsTotal / this.pageSize));
  }

  get unlimited(): boolean {
    return this.quota?.live_screencast_unlimited === true;
  }

  get usedSeconds(): number | null {
    return this.quotaUnavailable ? null : this.parseNumber(this.quota?.live_screencast_used_seconds_today);
  }

  get limitMinutes(): number | null {
    return this.quotaUnavailable ? null : this.parseNumber(this.quota?.live_screencast_daily_limit_minutes);
  }

  get remainingSeconds(): number | null {
    if (this.quotaUnavailable || this.unlimited) {
      return null;
    }
    return this.parseNumber(this.quota?.live_screencast_remaining_seconds_today);
  }

  get quotaPercent(): number {
    const used = this.usedSeconds;
    const limit = this.limitMinutes;
    if (used == null || limit == null || limit <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((used / (limit * 60)) * 100));
  }

  get exhausted(): boolean {
    return !this.unlimited && !this.quotaUnavailable && this.remainingSeconds === 0 && this.usedSeconds != null;
  }

  get nearLimit(): boolean {
    return !this.unlimited && isNearQuotaLimit(this.usedSeconds, this.limitMinutes, this.remainingSeconds);
  }

  get lastUpdateLabel(): string {
    if (!this.quotaLastSuccessAt) {
      return 'update time unavailable';
    }
    return this.quotaLastSuccessAt.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  get resetLabel(): string {
    const reset = this.quota?.live_screencast_quota_resets_at;
    if (!reset) {
      return this.timezone;
    }
    const dt = DateTime.fromISO(reset, { setZone: true }).setZone(this.timezone);
    if (!dt.isValid) {
      return this.timezone;
    }
    return `Resets ${dt.toFormat('LLL d, h:mm a')} · ${this.timezone}`;
  }

  refreshAll(): void {
    this.refreshQuota();
    this.refreshCurrentTab();
  }

  setTab(tab: AuditLogsTab): void {
    if (tab === 'settings' && !this.settingsTabEnabled) {
      return;
    }
    if (this.activeTab === tab) {
      return;
    }
    const previousDefault = this.defaultPresetForTab(this.activeTab);
    this.activeTab = tab;
    this.persistActiveTab(tab);
    const nextDefault = this.defaultPresetForTab(tab);
    // When landing on Settings, prefer Last 7 days unless the user already picked a non-default range.
    if (tab === 'settings' && this.appliedPreset === previousDefault) {
      this.appliedPreset = nextDefault;
      this.filterForm.patchValue({ datePreset: nextDefault }, { emitEvent: false });
      const end = DateTime.now().setZone(this.timezone);
      const start = end.minus({ days: 6 });
      this.customStart = start.toISODate() || '';
      this.customEnd = end.toISODate() || '';
      this.updateRangeCaption(this.customStart, this.customEnd);
      this.settingsPage = 1;
    }
    this.refreshCurrentTab();
  }

  private refreshCurrentTab(): void {
    if (this.activeTab === 'screenshot') {
      this.screenshotLoad$.next();
      return;
    }
    if (this.activeTab === 'settings') {
      this.settingsLoad$.next();
      return;
    }
    this.sessionLoad$.next();
  }

  private defaultPresetForTab(tab: AuditLogsTab): ScreencastDatePreset {
    return tab === 'settings' ? 'week' : 'today';
  }

  private readStoredTab(): AuditLogsTab {
    try {
      const raw = sessionStorage.getItem(TAB_STORAGE_KEY);
      if (raw === 'screencast' || raw === 'screenshot' || (raw === 'settings' && this.settingsTabEnabled)) {
        return raw;
      }
    } catch {
      // ignore storage errors
    }
    return 'screencast';
  }

  private persistActiveTab(tab: AuditLogsTab): void {
    try {
      sessionStorage.setItem(TAB_STORAGE_KEY, tab);
    } catch {
      // ignore storage errors
    }
  }

  applyCustomDates(): void {
    const start = this.customStart;
    const end = this.customEnd;
    if (!start || !end) {
      this.dateError = 'Choose both a start and end date.';
      return;
    }
    if (isReversedDateRange(start, end)) {
      this.dateError = 'Start date must be on or before end date.';
      return;
    }
    this.dateError = '';
    this.appliedPreset = 'custom';
    this.filterForm.patchValue({ datePreset: 'custom' }, { emitEvent: false });
    this.page = 1;
    this.screenshotPage = 1;
    this.settingsPage = 1;
    this.refreshCurrentTab();
  }

  cancelCustomDates(): void {
    this.showCustomDates = this.appliedPreset === 'custom';
    this.filterForm.patchValue({ datePreset: this.appliedPreset }, { emitEvent: false });
    this.dateError = '';
  }

  clearFilters(): void {
    const defaultPreset = this.defaultPresetForTab(this.activeTab);
    this.appliedPreset = defaultPreset;
    this.dateError = '';
    this.showCustomDates = false;
    this.page = 1;
    this.screenshotPage = 1;
    this.settingsPage = 1;
    this.expandedSettingsId = null;
    if (defaultPreset === 'week') {
      const end = DateTime.now().setZone(this.timezone);
      const start = end.minus({ days: 6 });
      this.customStart = start.toISODate() || '';
      this.customEnd = end.toISODate() || '';
    } else {
      const today = DateTime.now().setZone(this.timezone).toISODate() || '';
      this.customStart = today;
      this.customEnd = today;
    }
    this.filterForm.reset({
      employee_user_id: '',
      admin_user_id: '',
      mode: '',
      status: '',
      event_type: '',
      datePreset: defaultPreset,
    });
    this.refreshCurrentTab();
  }

  get hasActiveFilters(): boolean {
    const val = this.filterForm?.value || {};
    const defaultPreset = this.defaultPresetForTab(this.activeTab);
    return !!(
      val.employee_user_id ||
      val.admin_user_id ||
      val.mode ||
      val.status ||
      val.event_type ||
      this.appliedPreset !== defaultPreset
    );
  }

  onEmployeeSelected(employeeId: any): void {
    const id = employeeId === null || employeeId === undefined ? '' : String(employeeId);
    this.filterForm.patchValue({ employee_user_id: id });
  }

  getUserForProfile(person: ScreencastPersonIdentity | { id: number; name: string | null } | null | undefined): any {
    if (!person) {
      return null;
    }
    const found = this.userMap.get(Number(person.id));
    if (found) {
      return found;
    }
    return {
      id: person.id,
      name: person.name || `#${person.id}`,
    };
  }

  onPaginateChange(event: PageEvent): void {
    this.page = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.sessionLoad$.next();
  }

  onScreenshotPaginateChange(event: PageEvent): void {
    this.screenshotPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.screenshotLoad$.next();
  }

  onSettingsPaginateChange(event: PageEvent): void {
    this.settingsPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.settingsLoad$.next();
  }

  prevPage(): void {
    if (this.page <= 1) {
      return;
    }
    this.page -= 1;
    this.sessionLoad$.next();
  }

  nextPage(): void {
    if (this.page >= this.pages) {
      return;
    }
    this.page += 1;
    this.sessionLoad$.next();
  }

  prevScreenshotPage(): void {
    if (this.screenshotPage <= 1) {
      return;
    }
    this.screenshotPage -= 1;
    this.screenshotLoad$.next();
  }

  nextScreenshotPage(): void {
    if (this.screenshotPage >= this.screenshotPages) {
      return;
    }
    this.screenshotPage += 1;
    this.screenshotLoad$.next();
  }

  toggleSettingsDetails(eventId: number): void {
    this.expandedSettingsId = this.expandedSettingsId === eventId ? null : eventId;
  }

  settingsEmployeesPreview(event: SettingsAuditEvent, limit = 3): string {
    if ((event.action_type === 'company' || event.scope === 'company') && !(event.employee_count > 0)) {
      return 'Company defaults';
    }
    const names = (event.employees || [])
      .map((person) => this.personName(person))
      .filter((name) => !!name && name !== '—');
    if (!names.length) {
      return `${event.employee_count || 0} employee${(event.employee_count || 0) === 1 ? '' : 's'}`;
    }
    const shown = names.slice(0, limit);
    const remaining = Math.max(0, (event.employee_count || names.length) - shown.length);
    return remaining > 0 ? `${shown.join(', ')} +${remaining} more` : shown.join(', ');
  }

  settingsSummaryPreview(event: SettingsAuditEvent, limit = 3): string {
    const rows = event.settings_summary || [];
    if (!rows.length) {
      return 'No setting values recorded';
    }
    const shown = rows.slice(0, limit).map((row) => this.settingsDiffLabel(row));
    const remaining = Math.max(0, rows.length - shown.length);
    return remaining > 0 ? `${shown.join(' · ')} · +${remaining} more` : shown.join(' · ');
  }

  settingsDiffLabel(row: { label: string; value: string; old_value?: string | null; new_value?: string | null }): string {
    const next = row.new_value || row.value;
    if (row.old_value) {
      return `${row.label}: ${row.old_value} → ${next}`;
    }
    return `${row.label}: ${next}`;
  }

  screenshotActionLabel(eventType: string | null | undefined): string {
    return eventType === 'screenshot_delete' ? 'Delete' : 'On demand';
  }

  screenshotSourceLabel(source: string | null | undefined): string {
    const labels: Record<string, string> = {
      employees_list: 'Employees list',
      profile: 'Profile',
      tabs: 'Employee page',
      screenshot_viewer: 'Screenshot viewer',
      live_monitoring: 'Live monitoring',
      onboarding: 'Onboarding',
      screenshots_page: 'Screenshots',
    };
    return source ? (labels[source] || source.replace(/_/g, ' ')) : '—';
  }

  capturedTimeLabel(raw: string | null | undefined): string {
    if (!raw) {
      return '';
    }
    const sql = DateTime.fromSQL(raw, { zone: this.timezone });
    if (sql.isValid) {
      return sql.toFormat('dd LLL yyyy, h:mm a');
    }
    const iso = DateTime.fromISO(raw, { setZone: true }).setZone(this.timezone);
    return iso.isValid ? iso.toFormat('dd LLL yyyy, h:mm a') : raw;
  }

  openScreenshot(event: ScreenshotAuditEvent): void {
    if (!event.image_url) {
      return;
    }
    this.screenshotFancybox.open({
      items: [{
        id: event.screenshot_id,
        image_url: event.image_url,
        thumbnail: event.image_url,
        large: event.image_url,
        user_id: event.employee?.id,
        ist_time: event.screenshot_ist_time,
        type: 'admin-request',
        app_name: event.app_name || 'On demand',
      }],
      startIndex: 0,
      userList: this.employees,
      showEmployee: true,
      shiftTimezone: this.timezone,
    });
  }

  actorRoleLabel(role: string | null | undefined): string {
    const first = (role || '').split(',')[0];
    if (!first) {
      return '';
    }
    return first.charAt(0).toUpperCase() + first.slice(1);
  }

  clock(iso: string | null | undefined): string {
    return formatClock(iso, this.timezone);
  }

  dateLine(iso: string | null | undefined): string {
    if (!iso) {
      return '—';
    }
    const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(this.timezone);
    return dt.isValid ? dt.toFormat('dd LLL yyyy') : '—';
  }

  timeLine(iso: string | null | undefined): string {
    if (!iso) {
      return '';
    }
    const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(this.timezone);
    return dt.isValid ? dt.toFormat('h:mm a') : '';
  }

  /** Start→end with seconds for the When column (date is on the line above). */
  sessionTimeRange(session: Pick<ScreencastSessionSummary, 'requested_at_utc' | 'ended_at_utc' | 'failed_at_utc' | 'status'>): string {
    const start = this.exactTime(session.requested_at_utc);
    if (!start) {
      return '—';
    }
    const endIso = session.ended_at_utc || session.failed_at_utc;
    const end = this.exactTime(endIso);
    if (!end) {
      return session.status === 'Live' || session.status === 'Connecting' ? `${start} · now` : start;
    }
    return `${start} – ${end}`;
  }

  private exactTime(iso: string | null | undefined): string {
    if (!iso) {
      return '';
    }
    const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(this.timezone);
    return dt.isValid ? dt.toFormat('h:mm:ss a') : '';
  }

  personName(person: { id: number; name: string | null } | null | undefined): string {
    if (!person) {
      return '—';
    }
    return person.name || `#${person.id}`;
  }

  trackBySession(index: number, session: ScreencastSessionSummary): string {
    return session?.session_id || String(index);
  }

  trackByScreenshotEvent(index: number, event: ScreenshotAuditEvent): string | number {
    return event?.id || index;
  }

  trackBySettingsEvent(index: number, event: SettingsAuditEvent): string | number {
    return event?.id || index;
  }

  trackByUserId(index: number, user: any): number {
    return user?.id ?? index;
  }

  private loadDirectory(): void {
    this.projectService.getUserList(0, 0, false).pipe(
      catchError(() => of([])),
      takeUntil(this.destroy$)
    ).subscribe((rows: any) => {
      const rawList = Array.isArray(rows) ? rows : ((rows as any)?.data || (rows as any)?.users || []);
      this.employees = rawList
        .filter((u: any) => u && Number(u.id) > 0)
        .map((u: any) => ({
          ...u,
          id: Number(u.id),
          name: String(u.name || u.first_name || u.email || '').trim() || `#${u.id}`,
        }));
      this.employees.forEach((u: any) => this.userMap.set(Number(u.id), u));
    });

    forkJoin({
      company: this.projectService.getFilteredTeam('company').pipe(catchError(() => of([]))),
      manager: this.projectService.getFilteredTeam('manager').pipe(catchError(() => of([]))),
      lead: this.projectService.getFilteredTeam('lead').pipe(catchError(() => of([]))),
    }).pipe(takeUntil(this.destroy$)).subscribe((groups) => {
      const merged = new Map<number, any>();
      const addRequesters = (list: any) => {
        (Array.isArray(list) ? list : []).forEach((u: any) => {
          if (u && Number(u.id) > 0) {
            const normalized = {
              ...u,
              id: Number(u.id),
              name: String(u.name || u.first_name || u.email || '').trim() || `#${u.id}`,
            };
            merged.set(normalized.id, normalized);
            if (!this.userMap.has(normalized.id)) {
              this.userMap.set(normalized.id, normalized);
            }
          }
        });
      };
      addRequesters(groups.company);
      addRequesters(groups.manager);
      addRequesters(groups.lead);
      this.requesters = Array.from(merged.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });
  }

  private refreshQuota(): void {
    this.quotaUnavailable = !this.lastQuotaPayload;
    this.quotaService.fetchQuota().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (!res?.success || !res.quota) {
          this.markQuotaFailed();
          return;
        }
        this.lastQuotaPayload = res.quota;
        this.quota = res.quota;
        this.quotaUnavailable = false;
        this.quotaStale = false;
        this.quotaLastSuccessAt = new Date();
        this.quotaAsOfUtc = res.quota.as_of_utc || null;
      },
      error: () => this.markQuotaFailed(),
    });
  }

  private markQuotaFailed(): void {
    this.quotaStale = !!this.lastQuotaPayload;
    this.quotaUnavailable = !this.lastQuotaPayload;
    this.quota = this.lastQuotaPayload;
  }

  private fetchSessions() {
    // Keep existing rows visible while refreshing — only skeleton on first load.
    if (!this.sessions.length) {
      this.loading = true;
    }
    const range = presetDateRange(
      this.appliedPreset,
      this.timezone,
      this.customStart,
      this.customEnd
    );
    const raw = this.filterForm.value || {};
    return this.sessionsApi.list({
      timezone: this.timezone,
      start_date: range.start,
      end_date: range.end,
      employee_user_id: raw.employee_user_id ? Number(raw.employee_user_id) : undefined,
      admin_user_id: raw.admin_user_id ? Number(raw.admin_user_id) : undefined,
      mode: raw.mode || undefined,
      status: raw.status || undefined,
      page: this.page,
      per_page: this.pageSize,
    }).pipe(
      catchError((err) => of({
        success: false,
        error: err?.error?.message || 'Unable to load screencast logs',
      })),
      map((res: any) => {
        if (res?.success === false || res?.error) {
          return {
            error: res.error || 'Unable to load screencast logs',
            sessions: [] as ScreencastSessionSummary[],
            total: 0,
            page: this.page,
            timezone: this.timezone,
            startDate: range.start,
            endDate: range.end,
          };
        }
        return {
          error: null as string | null,
          sessions: Array.isArray(res?.sessions) ? res.sessions : [],
          total: Number(res?.total) || 0,
          page: Number(res?.page) || this.page,
          timezone: res?.timezone || this.timezone,
          startDate: res?.start_date || range.start,
          endDate: res?.end_date || range.end,
        };
      })
    );
  }

  private fetchScreenshotEvents() {
    if (!this.screenshotEvents.length) {
      this.screenshotLoading = true;
    }
    const range = presetDateRange(
      this.appliedPreset,
      this.timezone,
      this.customStart,
      this.customEnd
    );
    const raw = this.filterForm.value || {};
    return this.screenshotAudit.list({
      timezone: this.timezone,
      start_date: range.start,
      end_date: range.end,
      employee_user_id: raw.employee_user_id ? Number(raw.employee_user_id) : undefined,
      actor_user_id: raw.admin_user_id ? Number(raw.admin_user_id) : undefined,
      event_type: raw.event_type || undefined,
      page: this.screenshotPage,
      per_page: this.pageSize,
    }).pipe(
      catchError((err) => of({
        success: false,
        error: err?.error?.message || 'Unable to load screenshot logs',
      })),
      map((res: any) => {
        if (res?.success === false || res?.error) {
          return {
            error: res.error || 'Unable to load screenshot logs',
            events: [] as ScreenshotAuditEvent[],
            total: 0,
            page: this.screenshotPage,
            timezone: this.timezone,
            startDate: range.start,
            endDate: range.end,
          };
        }
        return {
          error: null as string | null,
          events: Array.isArray(res?.events) ? res.events : [],
          total: Number(res?.total) || 0,
          page: Number(res?.page) || this.screenshotPage,
          timezone: res?.timezone || this.timezone,
          startDate: res?.start_date || range.start,
          endDate: res?.end_date || range.end,
        };
      })
    );
  }

  private fetchSettingsEvents() {
    if (!this.settingsEvents.length) {
      this.settingsLoading = true;
    }
    const range = presetDateRange(
      this.appliedPreset,
      this.timezone,
      this.customStart,
      this.customEnd
    );
    const raw = this.filterForm.value || {};
    return this.settingsAudit.list({
      timezone: this.timezone,
      start_date: range.start,
      end_date: range.end,
      employee_user_id: raw.employee_user_id ? Number(raw.employee_user_id) : undefined,
      actor_user_id: raw.admin_user_id ? Number(raw.admin_user_id) : undefined,
      page: this.settingsPage,
      per_page: this.pageSize,
    }).pipe(
      catchError((err) => of({
        success: false,
        error: err?.error?.error?.message || err?.error?.message || err?.error?.code || 'Unable to load settings logs',
      })),
      map((res: any) => {
        if (res?.success === false || res?.error || res?.code) {
          const errMsg = typeof res.error === 'string'
            ? res.error
            : (res.error?.message || res.message || 'Unable to load settings logs');
          return {
            error: errMsg,
            events: [] as SettingsAuditEvent[],
            total: 0,
            page: this.settingsPage,
            timezone: this.timezone,
            startDate: range.start,
            endDate: range.end,
          };
        }
        return {
          error: null as string | null,
          events: Array.isArray(res?.events) ? res.events : [],
          total: Number(res?.total) || 0,
          page: Number(res?.page) || this.settingsPage,
          timezone: res?.timezone || this.timezone,
          startDate: res?.start_date || range.start,
          endDate: res?.end_date || range.end,
        };
      })
    );
  }

  private updateRangeCaption(start?: string | null, end?: string | null): void {
    const labels: Record<ScreencastDatePreset, string> = {
      today: `Today · ${start || ''}`.trim(),
      yesterday: `Yesterday · ${start || ''}`.trim(),
      week: `${start} – ${end}`,
      month: `${start} – ${end}`,
      custom: `${start} – ${end}`,
    };
    this.appliedRangeLabel = `${labels[this.appliedPreset]} · Times in ${this.timezone}`;
  }

  private parseNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
}
